import React, { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { useSearchParams } from 'react-router-dom';

import {
  trackCheckInUpdatePersonSuccess,
  trackCheckInUpdatePersonError,
} from '../../../analytics';

import {
  Form,
  InputGroup,
  FloatingLabel,
  InputGroupText,
} from '../../../components/form';
import { Button } from '../../../components/general';
import { ConfirmModal, ErrorModal } from '../../../components/modals';

import { URL } from '../../../constants';

import { updatePersonDetails } from '../../../services/checkInService';

import { PublicEvent, Face } from '../../../types';
import { goTo, isEmpty, getUrl } from '../../../utils';

import { validateForm } from '../components/checkInValidations';
import { getTipsList } from '../components/getCheckinText';

interface Props {
  event: PublicEvent;
  face: Face;
  refetchFace?: () => void;
  isPreview?: boolean;
  stepText?: number;
}

const DetailsStage: React.FC<Props> = (props) => {
  const { event, face, isPreview, stepText } = props;

  const [searchParams] = useSearchParams();
  const isNew = searchParams.get('isNew') === 'true';

  const [personData, setPersonData] = useState({
    name: isNew ? '' : (face.name ?? ''),
    phoneNumber: face.phoneNumber ?? '',
    instagram: face.instagram ?? '',
    customFieldData: face.customFieldData ?? '',
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isNew)
      window.history.replaceState(
        null,
        '',
        getUrl(URL.CHECK_IN, event.eventId),
      );
  }, [event.eventId, isNew]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setPersonData((prevData) => ({ ...prevData, [name]: value }));

    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isPreview) return;

    const validationErrors = await validateForm({
      type: 'details',
      formData: personData,
    });

    // if errors found – set errors and stop here
    if (!isEmpty(validationErrors)) {
      setFormErrors(validationErrors);
      return;
    }

    setIsProcessing(true);
    setShowConfirmModal(true);

    try {
      await updatePersonDetails({
        eventId: event.eventId,
        elementId: face.elementId,
        personData,
        trackOnSuccess: trackCheckInUpdatePersonSuccess,
        trackOnError: trackCheckInUpdatePersonError,
      });
    } catch (err: any) {
      setShowConfirmModal(false);
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmit} formClassName="w-full">
        <h5>
          {!!stepText && parse(`<b>Step ${stepText}</b>: `)}Fill in your details
        </h5>

        <FloatingLabel
          label="First name"
          name="name"
          value={personData.name}
          onChange={handleChange}
          isInvalid={!!formErrors?.name}
          errMsg={formErrors?.name}
          required
        />

        <FloatingLabel
          label="Phone number"
          name="phoneNumber"
          value={personData.phoneNumber}
          onChange={handleChange}
        />

        {event.customFieldLabel && (
          <FloatingLabel
            label={event.customFieldLabel}
            name="customFieldData"
            value={personData.customFieldData}
            onChange={handleChange}
          />
        )}

        <div className="flex flex-col gap-2">
          <div>
            Be featured, get exclusive content or sneak peek behind the scenes
            of this event via Instagram!
          </div>

          <InputGroup>
            <InputGroupText>@</InputGroupText>

            <FloatingLabel
              label="Instagram"
              name="instagram"
              value={personData.instagram}
              onChange={handleChange}
              className="w-full"
            />
          </InputGroup>
        </div>

        <Button isSubmitBtn isProcessing={isProcessing}>
          Submit
        </Button>
      </Form>

      <ConfirmModal
        show={showConfirmModal}
        isProcessing={isProcessing}
        heading={
          isProcessing
            ? "We're updating your details"
            : `Great! You have successfully checked in to ${event.name}, ${personData.name}.`
        }
        subheading={
          !isProcessing &&
          'Feel free to <b>close this page</b>, and keep an eye on your inbox for your personalised video.'
        }
        tipsList={getTipsList()}
        btns={
          !isProcessing
            ? [{ label: 'OK', onClick: () => goTo('https://crowdclip.com/') }]
            : undefined
        }
      />

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        btn={
          <Button onClick={() => setError('')} className="w-full">
            OK
          </Button>
        }
      />
    </React.Fragment>
  );
};

export default DetailsStage;
